import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePayment } from '../contexts/PaymentContext';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, DollarSign, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import axios from 'axios';

const Subscription = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const { cancelSubscription } = usePayment();
  const { user } = useAuth();
  const navigate = useNavigate();

  // API base URL from environment
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        if (!user) {
          setError('You must be logged in to view subscriptions');
          setLoading(false);
          return;
        }

        // Get the Firebase ID token
        const token = await user.getIdToken();

        if (!token) {
          throw new Error('No authorization token available');
        }

        // Fetch from API endpoint
        const response = await axios.get(`${API_URL}/api/payments/subscriptions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (Array.isArray(response.data)) {
          setSubscriptions(response.data);
        } else {
          setSubscriptions([]);
        }
      } catch (err) {
        setError('Failed to load subscription data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, [API_URL]);

  const handleCancelSubscription = async (subscriptionId) => {
    try {
      setCancellingId(subscriptionId);

      // Wait a bit to simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));

      // Show notification that feature isn't working
      setError('Subscription cancellation is currently disabled. Please contact support for assistance if you need to cancel your subscription.');

      // Clear the error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setCancellingId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2" />
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Subscriptions</h1>
        <p className="text-gray-600">Manage your project subscriptions and billing information</p>
      </div>

      {subscriptions.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-gray-100 p-3">
              <Calendar className="h-8 w-8 text-gray-500" />
            </div>
          </div>
          <h2 className="text-xl font-semibold mb-2">No Active Subscriptions</h2>
          <p className="text-gray-600 mb-6">You don't have any active subscriptions at the moment.</p>
          <button
            onClick={() => navigate('/checkout')}
            className="px-6 py-3 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
          >
            Subscribe Now
          </button>
        </div>
      ) : (
        <div>
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Project
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purchase Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Next Billing Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subscriptions.map((subscription) => (
                    <tr key={subscription.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{subscription.projectName}</div>
                        <div className="text-sm text-gray-500">ID: {subscription.projectId}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(subscription.startDate)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(subscription.nextBillingDate)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm font-medium text-gray-900">
                          <DollarSign className="h-4 w-4 mr-1" />
                          {subscription.amount}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {subscription.status === 'active' ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Cancelled
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {subscription.status === 'active' ? (
                          <button
                            onClick={() => handleCancelSubscription(subscription.id)}
                            disabled={cancellingId === subscription.id}
                            className="text-red-600 hover:text-red-800 font-medium flex items-center"
                          >
                            {cancellingId === subscription.id ? (
                              <>
                                <div className="animate-spin h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full mr-2"></div>
                                Processing...
                              </>
                            ) : (
                              <>
                                <Trash2 className="h-4 w-4 mr-1" />
                                Cancel
                              </>
                            )}
                          </button>
                        ) : (
                          <div className="text-gray-500 flex items-center">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Cancelled
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h2 className="text-xl font-semibold mb-4">Subscription Information</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-primary-100 flex items-center justify-center mt-0.5">
                  <CheckCircle className="h-4 w-4 text-primary-700" />
                </div>
                <p className="ml-3 text-gray-700">
                  All subscriptions are billed monthly at $99 per project.
                </p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-primary-100 flex items-center justify-center mt-0.5">
                  <CheckCircle className="h-4 w-4 text-primary-700" />
                </div>
                <p className="ml-3 text-gray-700">
                  You can cancel your subscription at any time. Your subscription will remain active until the end of the current billing period.
                </p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-primary-100 flex items-center justify-center mt-0.5">
                  <CheckCircle className="h-4 w-4 text-primary-700" />
                </div>
                <p className="ml-3 text-gray-700">
                  For any billing issues or questions, please contact our support team.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 text-right">
            <button
              onClick={() => navigate('/checkout')}
              className="px-6 py-3 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
            >
              Add New Project
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription; 